<template>
  <div class="inner">
    <v-alert border="top" color="error-lighten-2" v-if="!loading && error">{{ error }}</v-alert>
    <v-skeleton-loader type="table-tbody" v-if="loading" />
    <div v-if="accountList && accountList.length > 0">
      <table v-tablesort class="apptable sortable dense --tight-first mt-2">
        <thead>
          <tr>
            <td class="tw:rounded-tl-md tw:rounded-bl-md">Account Name</td>
            <td class="nosort">Coverages</td>
            <td class="tw:rounded-tr-md tw:rounded-br-md">Parent Company</td>
          </tr>
        </thead>
        <tbody>
          <tr v-for="account in accountList" :key="account.accountID">
            <td>
              <div class="name-wrapper">
                <router-link
                  class="name"
                  :to="`/account/${
                    account.childAccountID === null ? account.accountID : account.childAccountID
                  }`"
                  data-test="account-name"
                >
                  <div class="tw:font-medium tw:tracking-wide">{{ account.childAccountDisplayName || account.childAccountName || account.accountDisplayName || account.accountName }}</div>
                  <div v-if="account.childAccountID && account.childAccountDisplayName" class="tw:italic tw:font-light tw:text-xs tw:ml-2">{{ account.childAccountName }}</div>
                  <div v-else-if="!account.childAccountID && account.accountDisplayName" class="tw:italic tw:font-light tw:text-xs tw:ml-2">{{ account.accountName }}</div>
                </router-link>
                <v-chip
                  density="compact"
                  variant="outlined"
                  color="blue-lighten-2"
                  class="prospect"
                  v-show="account.accountStatus === 'P' && !IsUserAClientAccount"
                  data-test="prospect-chip"
                  >prospect</v-chip
                >
              </div>
            </td>
            <td>
              <div class="coverages tw:m-0" v-if="account.Coverages.length > 0">
                <v-chip
                  class="coverage-chip tw:my-0.5"
                  :class="{ external: isExternal }"
                  :color="isExternal ? 'default' : chipColor"
                  density="compact"
                  variant="outlined"
                  v-for="{ key, coverageType, isExternal } in account.Coverages"
                  :key="key"
                >
                  <div>{{ coverageType }}</div>
                </v-chip>
              </div>
            </td>
            <td>
              <div class="name-wrapper">
                <router-link
                  class="name"
                  :to="`/parent/account/${account.accountID}/overview`"
                  data-test="account-name"
                >
                  <div>{{ account.childAccountID === null ? account.childAccountName : (account.accountDisplayName || account.accountName) }}</div>
                </router-link>
                <v-chip
                  density="compact"
                  variant="outlined"
                  color="blue-lighten-2"
                  class="prospect"
                  v-if="account.accountStatus === 'P' && !IsUserAClientAccount"
                  data-test="prospect-chip"
                >
                  prospect
                </v-chip>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
<script lang="ts">
import { accountService } from '@/services/api';
import { ClientAccount } from '@/types/AccountSummary';
import { PolicySourceEpic } from '@/utilities/PolicySources';
import { Store } from '@/store';
import { clientViewService } from '@/services/v2/ClientViewService';
import { chipColor } from '@/utilities/Color';
import { BenefitsReadCoverageTypes, FeesReadCoverageTypes } from '@/types/CoverageTypes';
import { UserHasPermission, IsUserAClient } from '@/utilities/AuthUtils';
import { UserPermission } from '@/types/UserPermissions';

export default {
  name: 'ClientView',
  data: () => ({
    accountList: [] as any[],
    loading: true,
    error: '',
    chipColor
  }),
  computed: {
    IsUserAClientAccount() {
      return IsUserAClient(Store.state.user);
    },
  },
  methods: {
    async getClientAccounts(): Promise<ClientAccount[]> {

      let clientAccounts: ClientAccount[];

      if (!Store.state.clientViewState.isClientViewEnabled) {
        clientAccounts = await accountService.getClientAccounts();
      } else {
        const accountIds = Store.state.clientViewState.activeView?.allowedAccountIds;
        if (!accountIds) {
          console.error('No account ids found in client view state');
          return [];
        }

        const accounts = await clientViewService.getClientAccountsByIds(accountIds);

        const transformedAccounts = accounts.reduce((acc, account) => {
          if (!acc.has(account.accountId)) {
            acc.set(account.accountId, {
              accountID: account.parentAccountId ? account.parentAccountId : account.accountId,
              accountName: account.parentAccountId ? account.parentAccountName! : account.accountName,
              accountDisplayName: account.parentAccountId ? account.parentAccountDisplayName : account.accountDisplayName,
              childAccountID: account.parentAccountId ? account.accountId : null,
              childAccountName: account.parentAccountId ? account.accountName : null,
              childAccountDisplayName: account.parentAccountId ? account.accountDisplayName : null,
              accountStatus: account.accountStatus,
              coverageTypes: `${account.coverageType}||${account.policySourceId}`,
              childCoverageTypes: account.parentAccountId ? `${account.coverageType}||${account.policySourceId}` : '',
            });
          } else {
            const found = acc.get(account.accountId);
            found!.coverageTypes += `,${account.coverageType}||${account.policySourceId}`;
            found!.childCoverageTypes += account.parentAccountId ? `,${account.coverageType}||${account.policySourceId}` : '';
            acc.set(account.accountId, found!);
          }
          return acc;
        }, new Map<number, ClientAccount>());

        clientAccounts = Array.from(transformedAccounts.values());
      }

      return clientAccounts
        .map(a => {
          return {
            ...a,
            rowValue: a.childAccountDisplayName || a.childAccountName || a.accountDisplayName || a.accountName,
          };
        })
        .sort((a, b) => a.rowValue.localeCompare(b.rowValue))
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .map(({ rowValue, ...rest }) => rest);

    },
    async getAccountList() {
      this.loading = true;
      const list = await this.getClientAccounts();
      this.accountList = list.map((account) => {
        const coverageTypeStr = account.childAccountID === null ? account.coverageTypes : account.childCoverageTypes;
        const coverageTypeDataList = coverageTypeStr.split(',').filter((t) => t !== '').sort();
        const coverageTypes = coverageTypeDataList
          //re-used from AccountList TODO: refactor
          .map((coverageData) => {
            if (coverageData.includes('||')) {
              const [coverageType, policySourceIDStr] = coverageData.split('||');
              let policySourceID = parseInt(policySourceIDStr);
              if (isNaN(policySourceID)) {
                policySourceID = PolicySourceEpic;
              }
              return { key: coverageData, coverageType, isExternal: policySourceID !== PolicySourceEpic };
            } else {
              return { key: coverageData, coverageType: coverageData, isExternal: false };
            }
          })
          .filter((coverage) => {

            // the API already should do this filtering based on a user's permissions
            // this is mainly to support the "View as Client" use case

            if (FeesReadCoverageTypes.has(coverage.coverageType)) {
              return this.userHasFeesReadPermission();
            }

            if (BenefitsReadCoverageTypes.has(coverage.coverageType)) {
              return this.userHasBenefitsReadPermission();
            }

            return true;
          })
          .sort((a, b) => (a.isExternal === b.isExternal ? 0 : a.isExternal ? 1 : -1));
        return { ...account, Coverages: coverageTypes };
      });
      this.loading = false;
    },
    userHasFeesReadPermission() {
      return UserHasPermission(Store.state.user, UserPermission.FeesRead);
    },
    userHasBenefitsReadPermission() {
      return UserHasPermission(Store.state.user, UserPermission.BenefitsRead);
    },
  },
  created() {
    this.getAccountList();
  },
};
</script>

<style scoped>
.coverages {
  margin-top: 1em;
  max-width: 20vw;
}

.coverages > .v-chip {
  margin-left: 0.5em;
  margin-right: 0.5em;
  flex: 1 0 auto;
}

.coverage-chip {
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.7em;
}

.coverage-chip.external {
  font-weight: 400;
}
</style>
